import { ref, watch } from 'vue'
import callSoundUrl from '@/assets/call.mp3'
import { onConversationAssigned } from '@/composables/useConversationRealtime'
import { getConversation } from '@/services/wootdashConversations'
import { currentOpenConvId, type ChatId } from '@/composables/useChatTrigger'

export interface AssignmentAlert {
  convId: ChatId
  contactName: string
}

// Estado de módulo (singleton) — igual ao padrão de useAutoLogout.ts: o modal
// (AssignmentAlertModal.vue) só lê, quem escreve é a detecção de atribuição em
// DashboardView.vue. Fila (não um único valor) porque mais de uma conversa pode
// ser atribuída entre um ciclo de polling e outro.
export const pendingAssignments = ref<AssignmentAlert[]>([])

export function pushAssignmentAlert(alert: AssignmentAlert) {
  if (pendingAssignments.value.some(a => a.convId === alert.convId)) return
  pendingAssignments.value.push(alert)
}

/**
 * Fecha o alerta atual (primeiro da fila) — chamado pelo clique em "Iniciar
 * atendimento" OU "Dispensar" (ambos fecham o popup, só "Iniciar" também abre
 * a conversa). Persiste o dismiss em call_conv (migração v28) por
 * conversa+agente — não pelo id da linha, que este módulo não guarda —
 * pra não reabrir o popup no próximo catch-up (ver getPendingCallConv em
 * useDashboardEngine.ts). Best-effort: falha de rede aqui não impede fechar
 * o popup localmente, só deixa de persistir (reaparece no próximo catch-up).
 */
// Fase 4.2 do plano de otimização de login (docs/planos/PLANO-OTIMIZACAO-LOGIN.md):
// chamado no logout — sem isso, um agente B logando na mesma aba depois de A
// herdaria a fila de popups "Novo atendimento atribuído" de A. Substituir o
// array (não só esvaziar) dispara o watch abaixo, que já para o call.mp3
// sozinho se estiver tocando.
export function resetAssignmentAlerts(): void {
  pendingAssignments.value = []
}

/** Fecha o alerta atual. Só local: o aviso vem ao vivo pelo SignalR (decisão de 01/10 — sem
 * recuperar os que chegaram com o agente deslogado; a conversa continua atribuída na lista). */
export function dismissCurrentAssignmentAlert() {
  pendingAssignments.value.shift()
}

let stopListening: (() => void) | null = null

/** Chamado por App.vue no login: avisa quando uma conversa é atribuída a ESTE agente (roteamento
 * automático, atribuição manual ou macro). Não avisa a conversa que ele já está com aberta. */
export function startAssignmentAlerts(token: string, myAgentId: string): void {
  stopListening?.()
  stopListening = onConversationAssigned(async evt => {
    if (!myAgentId || evt.agentId !== myAgentId) return
    if (currentOpenConvId.value === evt.conversationId) return
    const detail = await getConversation(token, evt.conversationId).catch(() => null)
    pushAssignmentAlert({ convId: evt.conversationId, contactName: detail?.contactName ?? '' })
  })
}

export function stopAssignmentAlerts(): void {
  stopListening?.()
  stopListening = null
  pendingAssignments.value = []
}

// Toque de "ligação" (call.mp3) repetindo a cada 5s enquanto o popup "Novo
// atendimento atribuído" estiver na tela — para sozinho quando a fila esvazia
// (agente clicou em "Iniciar atendimento" no último pendente). Silencioso em
// qualquer falha: mesma política de playNotificationSound, autoplay bloqueado
// pelo navegador não pode quebrar o fluxo.
let callAudio: HTMLAudioElement | null = null
let callInterval: ReturnType<typeof setInterval> | null = null
let callStopRequested = false

// "Destrava" o áudio no primeiro gesto do usuário (clique/tecla) na página —
// o popup de atribuição pode abrir sozinho via polling em segundo plano (ver
// catch-up em useDashboardEngine.ts), sem nenhum clique imediatamente antes,
// e a política de autoplay do navegador bloqueia play() de áudio COM som sem
// interação recente na página. Chamar play()+pause() aqui dentro de um
// listener de clique/tecla real conta como interação e libera as próximas
// chamadas programáticas de play() na mesma sessão. Só precisa rodar uma vez
// com sucesso — depois disso os listeners se removem.
let callAudioUnlocked = false
function tryUnlockCallAudio() {
  if (callAudioUnlocked) return
  try {
    if (!callAudio) callAudio = new Audio(callSoundUrl)
    const p = callAudio.play()
    if (p && typeof p.then === 'function') {
      p.then(() => {
        callAudioUnlocked = true
        if (callAudio) {
          callAudio.pause()
          callAudio.currentTime = 0
        }
        window.removeEventListener('pointerdown', tryUnlockCallAudio)
        window.removeEventListener('keydown', tryUnlockCallAudio)
      }).catch(() => { /* ainda bloqueado — tenta de novo no próximo gesto */ })
    }
  } catch { /* ambiente sem suporte a Audio (ex.: testes) — ignora */ }
}
if (typeof window !== 'undefined') {
  window.addEventListener('pointerdown', tryUnlockCallAudio)
  window.addEventListener('keydown', tryUnlockCallAudio)
}

function playCallSound() {
  callStopRequested = false
  try {
    if (!callAudio) callAudio = new Audio(callSoundUrl)
    callAudio.currentTime = 0
    const playPromise = callAudio.play()
    // play() é assíncrono — se stopCallSound() for chamado antes dele resolver
    // (corrida comum: pause() logo em seguida de play() pode ser ignorado/
    // enfileirado pelo navegador, deixando o áudio tocando mesmo já "parado"),
    // garante que pausa de novo assim que o play() efetivamente começar.
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise
        .then(() => {
          if (callStopRequested && callAudio) {
            callAudio.pause()
            callAudio.currentTime = 0
          }
        })
        .catch(err => {
          // Não relança (não pode quebrar o fluxo), mas loga — autoplay
          // bloqueado pelo navegador é a causa mais comum de "popup aparece
          // mas não toca som", e sem isso fica impossível confirmar em debug.
          console.warn('[call.mp3] play() falhou/bloqueado:', err)
        })
    }
  } catch (err) {
    console.warn('[call.mp3] erro ao iniciar áudio:', err)
  }
}

// Para o loop E o áudio que já está tocando — só cancelar o setInterval
// impedia as PRÓXIMAS repetições, mas deixava a reprodução em curso terminar
// sozinha (call.mp3 tem alguns segundos), então o agente ainda ouvia o toque
// depois de já ter clicado em "Iniciar atendimento"/"Dispensar".
function stopCallSound() {
  callStopRequested = true
  if (callInterval) {
    clearInterval(callInterval)
    callInterval = null
  }
  if (callAudio) {
    callAudio.pause()
    callAudio.currentTime = 0
  }
}

// Não usa o old/new value do watch pra detectar a transição vazia→cheia: como
// push/shift mutam o array em vez de substituí-lo, o Vue entrega a mesma
// referência em old e new (ambos já refletem o estado novo) — precisa de um
// flag próprio pra saber se já estava tocando.
let callAlertShowing = false
watch(pendingAssignments, list => {
  const showing = list.length > 0
  if (showing && !callAlertShowing) {
    playCallSound()
    if (callInterval) clearInterval(callInterval)
    callInterval = setInterval(playCallSound, 5000)
  } else if (!showing && callAlertShowing) {
    stopCallSound()
  }
  callAlertShowing = showing
}, { deep: true })
